import { Alert, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import type { FilterWidgetSettings } from "../../../../../../api/types/dashboardsTypes";
import SwitchField from "../../../../reporting/sv/components/common/SwitchField";
import { useDashboardContext } from "../../../contexts/DashboardContext";
import { getParentFilterOptions } from "../parentFilterOptions";

interface Props {
  widgetId: string;
  settings: FilterWidgetSettings;
  onSettingsChange: (settings: Partial<FilterWidgetSettings>) => void;
}

const LinkToParentFilter = ({ widgetId, settings, onSettingsChange }: Props) => {
  const { filterWidgets } = useDashboardContext();
  const parentOptions = useMemo(() => getParentFilterOptions(filterWidgets, widgetId), [filterWidgets, widgetId]);

  const [isLinked, setIsLinked] = useState(Boolean(settings.parentWidgetId));

  const handleLinkToParentChange = (linked: boolean) => {
    setIsLinked(linked);
    if (!linked) {
      onSettingsChange({ parentWidgetId: null });
    }
  };

  const selectedParentWidgetId = parentOptions.some((o) => o.widgetId === settings.parentWidgetId)
    ? settings.parentWidgetId
    : "";

  return (
    <Stack sx={{ gap: 1, width: "100%" }}>
      <SwitchField
        label="Link to Parent Filter"
        checked={isLinked}
        containerSx={{ width: "100%", flex: "0 0 auto" }}
        onChange={handleLinkToParentChange}
      />
      {isLinked && parentOptions.length === 0 && (
        <Alert severity="info">There are no other filter widgets on this dashboard to link to.</Alert>
      )}
      {isLinked && parentOptions.length > 0 && (
        <>
          <Typography variant="subtitle2">Parent Filter</Typography>
          <Select
            value={selectedParentWidgetId}
            fullWidth
            onChange={(e) => onSettingsChange({ parentWidgetId: e.target.value })}
          >
            {parentOptions.map((option) => (
              <MenuItem key={option.widgetId} value={option.widgetId}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="textSecondary">
            This widget follows the parent filter&apos;s selection.
          </Typography>
        </>
      )}
    </Stack>
  );
};

export default LinkToParentFilter;
